import { Box, Divider, Group } from '@mantine/core';
import { useDebouncedValue } from '@mantine/hooks';
import { observer } from 'mobx-react-lite';
import { useMemo } from 'react';
import { Portal } from 'react-portal';
import { useImpersonationBanner } from '../../ImpersonationBanner/useImpersonationBanner';
import type { RightSidebarStore } from '../../RightSidebar/store';
import { rightSidebarStore } from './../../RightSidebar';
import { useStyles } from './NavBarSidesheetControlsWrapper.styles';

function ActionsTogglesDivider({ collapsed }: { collapsed: boolean }) {
	const { showImpersonationBanner } = useImpersonationBanner();
	const { classes, theme } = useStyles(showImpersonationBanner);
	return (
		<Box
			h="md"
			className={classes.divider}
			style={{ opacity: collapsed ? '1' : '0' }}
		>
			<Divider
				orientation="vertical"
				size="xs"
				h="md"
				color={theme.other.getColor('border/primary/default')}
			/>
		</Box>
	);
}

interface NavBarSidesheetControlsWrapperProps {
	children: React.ReactNode;
	store?: RightSidebarStore;
}

export const NavBarSidesheetControlsWrapper = observer(
	({ children, store }: NavBarSidesheetControlsWrapperProps) => {
		store = store ?? rightSidebarStore;
		const { showImpersonationBanner } = useImpersonationBanner();
		const { classes } = useStyles(showImpersonationBanner);

		// We're unable to animate from "width: unset" to "width: 0". This was
		// causing some jank.
		const [delayedCollapse] = useDebouncedValue(store.collapsed, 50);

		const toggleChildren = useMemo(() => {
			const collapsed = delayedCollapse || false;
			return (
				<Group className={classes.sidesheetControlsWrapper}>
					<ActionsTogglesDivider collapsed={collapsed} />
					<Group className={classes.toggles}>{children}</Group>
				</Group>
			);
		}, [
			delayedCollapse,
			children,
			classes.sidesheetControlsWrapper,
			classes.toggles,
		]);
		return (
			<>
				<Group
					className={classes.sidesheetControlsPhantomElement}
					w={delayedCollapse ? 'unset' : 0}
					pl={delayedCollapse ? 'sm' : 0}
				>
					<ActionsTogglesDivider collapsed={delayedCollapse || false} />
					<Group className={classes.toggles}>{children}</Group>
				</Group>
				{delayedCollapse ? toggleChildren : <Portal>{toggleChildren}</Portal>}
			</>
		);
	}
);
